import { useState } from 'react'
import { db } from '../db/db'
import { sha256Hex } from '../utils/crypto'
import type { Cashier } from '../db/models'

import type { CashierPermissions } from '../db/models'
import { cx } from '../../ui/classes'

export interface ActiveCashier {
  user_id: string
  name: string
  permissions?: CashierPermissions
  /** Роль в облаке: admin получает все права (включая заявки с касс). */
  role?: 'admin' | 'cashier'
}

interface PinPadModalProps {
  isOpen: boolean
  onSuccess: (cashier: ActiveCashier) => void
  /**
   * Переход в первичную настройку. Показывается только при пустой локальной
   * базе; в рабочей кассе админка открывается по PIN директора/старшего.
   */
  onOpenAdmin?: () => void
  /** Закрыть без авторизации (подтверждение действия, а не вход). */
  onCancel?: () => void
}

/**
 * Тач-клавиатура оффлайн-авторизации кассира.
 *
 * Проверяет 4-значный PIN против локального справочника Dexie (cashiers),
 * сверяя SHA-256 хэш ввода с сохранённым pin_hash. Работает 100% оффлайн.
 */
export function PinPadModal({
  isOpen,
  onSuccess,
  onOpenAdmin,
  onCancel
}: PinPadModalProps) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const handleDigit = (digit: string) => {
    if (pin.length >= 4) return
    setError(null)
    const newPin = pin + digit
    setPin(newPin)
    if (newPin.length === 4) {
      void verifyPin(newPin)
    }
  }

  const handleClear = () => {
    setPin('')
    setError(null)
  }

  const verifyPin = async (inputPin: string) => {
    setLoading(true)
    setError(null)
    try {
      const pinHash = await sha256Hex(inputPin)

      // Ищем совпадение в локальной Dexie базе оффлайн-кассиров.
      const cashiers = await db.cashiers.toArray()
      const matched = cashiers.find(
        (c: Cashier) => c.pin_hash === pinHash || c.pin_hash?.endsWith(pinHash)
      )

      if (matched) {
        setPin('')
        onSuccess({
          user_id: matched.user_id,
          name: matched.name,
          permissions: matched.permissions,
          role: matched.role
        })
      } else {
        setError('Неверный PIN-код')
        setPin('')
      }
    } catch (err) {
      console.error('PIN verification error:', err)
      setError('Ошибка проверки PIN-кода')
      setPin('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-pin flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-[360px] rounded-panel bg-white p-8 text-center shadow-modal">
        <h2 className="mb-2 text-xl font-semibold text-ink">Авторизация</h2>
        <p className="mb-6 text-sm text-muted">Введите 4-значный персональный PIN</p>

        {/* Индикаторы ввода точек */}
        <div className="mb-6 flex justify-center gap-3">
          {[0, 1, 2, 3].map((idx) => (
            <div
              key={idx}
              className={cx(
                'h-4 w-4 rounded-pill transition-all',
                pin.length > idx ? 'bg-info' : 'bg-line'
              )}
            />
          ))}
        </div>

        {error && <div className="mb-4 text-small text-danger">{error}</div>}

        {/* Цифровая тач-сетка */}
        <div className="mb-4 grid grid-cols-3 gap-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button
              key={d}
              onClick={() => handleDigit(d)}
              disabled={loading}
              className="h-14 touch-manipulation rounded-panel border border-lineStrong bg-soft text-big font-semibold"
            >
              {d}
            </button>
          ))}
          <button
            onClick={handleClear}
            disabled={loading}
            className="h-14 rounded-panel border border-warnLine bg-red-100 text-sm font-semibold text-danger"
          >
            Сброс
          </button>
          <button
            onClick={() => handleDigit('0')}
            disabled={loading}
            className="h-14 rounded-panel border border-lineStrong bg-soft text-big font-semibold"
          >
            0
          </button>
          <div />
        </div>

        {onOpenAdmin && (
          <button
            onClick={onOpenAdmin}
            disabled={loading}
            className="mt-2 w-full rounded-panel border border-lineStrong bg-white px-3 py-3 text-sm font-semibold text-ink"
          >
            Первичная настройка облака
          </button>
        )}
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={loading}
            className="mt-2 w-full cursor-pointer rounded-panel border border-transparent bg-transparent px-3 py-3 text-sm font-semibold text-muted"
          >
            Отмена
          </button>
        )}
      </div>
    </div>
  )
}
